import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  MessageSquare,
  MessagesSquare,
  LayoutTemplate,
  FileText,
  Upload,
  Bot,
  FlaskConical,
  Send,
  Paperclip,
  X,
  Phone,
  ShieldCheck,
  Heart,
  Building2,
  CalendarDays,
  GraduationCap,
} from 'lucide-react';
import { agentsAPI, scriptsAPI, knowledgeAPI, chatbotAPI, templatesAPI } from './api';
import { AGENT_TEMPLATES, AgentSettingsCard } from './DemoCustomerAIPages';
import WorkflowEdit from '../WorkflowEdit';
import { getCurrentUserIdForCalls, getOutboundCallUrl } from '../../lib/apiConfig';

const TEMPLATE_ICONS = {
  building: Building2,
  heart: Heart,
  chat: MessagesSquare,
  shield: ShieldCheck,
  calendar: CalendarDays,
  grad: GraduationCap,
  phone: Phone,
  bot: Bot,
};
const TEMPLATE_TINTS = {
  building: 'bg-sky-50 text-sky-600',
  heart: 'bg-rose-50 text-rose-500',
  chat: 'bg-cyan-50 text-cyan-600',
  shield: 'bg-emerald-50 text-emerald-600',
  calendar: 'bg-orange-50 text-orange-500',
  grad: 'bg-violet-50 text-violet-600',
  phone: 'bg-amber-50 text-amber-600',
  bot: 'bg-teal-50 text-teal-600',
};

const EMPTY_FLOW = { nodes: [], edges: [], agent: {} };

// Voice test: agent calls FROM this number TO the fixed test line below.
const DEFAULT_FROM_NUMBER = '(805) 500-0806';
const TEST_CALL_NUMBER = '+1 555 555 1234'; // ← set this to your testing phone number

/* =========================================================
   Create New AI — an end-to-end build wizard. Each step's
   Next button persists that step to the backend and threads
   the result forward:
     Basic settings → create agent (agentId)
     Script         → save script (scriptId)
     Knowledge base → upload / select collections (collectionIds)
     Assign         → attach script + knowledge to the agent
     Test           → live chat test (no saving)
   ========================================================= */

/* ---------- Step 1: Choose a template ---------- */
const TemplateChooseStep = ({
  name,
  setName,
  desc,
  setDesc,
  icon,
  setIcon,
  settings,
  setSettings,
  templateKey,
  onPick,
  templates = AGENT_TEMPLATES,
}) => (
  <div className="mt-6 space-y-6">
    {/* 1. Template picker */}
    <div>
      <p className="text-[13px] font-semibold">Start from a template</p>
      <p className="mt-0.5 text-[12.5px] text-slate-400">
        Pick a ready-made AI employee — its script, rules, and settings are prefilled. You can change everything.
      </p>

      <button
        onClick={() => onPick('scratch')}
        className={`mt-3 flex w-full items-center gap-3.5 rounded-2xl border p-4 text-left transition ${
          templateKey === 'scratch'
            ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-400/40'
            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
          <Sparkles size={20} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold">Start from scratch</span>
          <span className="block text-[12.5px] text-slate-500">Build your own — a blank script and your own rules.</span>
        </span>
        {templateKey === 'scratch' && <Check size={18} className="shrink-0 text-teal-600" />}
      </button>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {templates.map((t) => {
          const Icon = TEMPLATE_ICONS[t.icon] || Bot;
          const on = templateKey === t.key;
          return (
            <button
              key={t.key || t._id}
              onClick={() => onPick(t.key)}
              className={`flex flex-col rounded-2xl border p-4 text-left transition ${
                on ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-400/40' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-start gap-3">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${TEMPLATE_TINTS[t.icon] || 'bg-slate-100 text-slate-500'}`}>
                  <Icon size={18} strokeWidth={1.9} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[14px] font-semibold">{t.name}</p>
                    {on && <Check size={16} className="shrink-0 text-teal-600" />}
                  </div>
                  <p className="text-[11.5px] text-slate-400">{t.category}</p>
                </div>
              </div>
              <p className="mt-2 text-[12.5px] leading-relaxed text-slate-500">{t.description}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {t.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-500">
                    {tag}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>

    {/* 2. Basic settings — appears once a template (or scratch) is chosen */}
    {templateKey && (
      <div className="space-y-4 border-t border-slate-100 pt-5">
        <p className="text-[13px] font-semibold">Basic settings</p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[12.5px] font-semibold text-slate-600">Agent name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Front Desk Assistant"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-slate-600">Description</label>
            <input
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What this agent does…"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
            />
          </div>
        </div>

        <div>
          <label className="text-[12.5px] font-semibold text-slate-600">Icon</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.keys(TEMPLATE_ICONS).map((key) => {
              const I = TEMPLATE_ICONS[key];
              return (
                <button
                  key={key}
                  onClick={() => setIcon(key)}
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ring-2 transition ${TEMPLATE_TINTS[key]} ${
                    icon === key ? 'ring-teal-500' : 'ring-transparent hover:ring-slate-200'
                  }`}
                >
                  <I size={18} strokeWidth={1.9} />
                </button>
              );
            })}
          </div>
        </div>

        <AgentSettingsCard value={settings} onChange={setSettings} />
      </div>
    )}
  </div>
);

/* ---------- Step 2: Script ---------- */
const ScriptEditStep = ({ mode, promptText, setPromptText, initialFlow, workflowLoadKey, scriptId, onWorkflowChange }) => {
  if (mode === 'workflow') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <p className="shrink-0 text-[12.5px] text-slate-500">Edit the conversation flow. Changes save automatically.</p>
        <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200">
          <WorkflowEdit
            initialData={initialFlow}
            workflowLoadKey={workflowLoadKey}
            scriptId={scriptId}
            hideAgentSettings
            hideSaveButton
            onWorkflowChange={onWorkflowChange}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full min-h-0 flex-col">
      <p className="shrink-0 text-[12.5px] text-slate-500">
        Review and edit what your AI says. This was prefilled from your template — tweak the tone, rules, and details.
      </p>
      <textarea
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
        placeholder="Describe your AI — its role, tone, what it should help with, and anything it must never do…"
        className="mt-3 min-h-0 w-full flex-1 resize-none rounded-xl border border-slate-200 px-3.5 py-3 font-mono text-[13px] leading-relaxed outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
      />
    </div>
  );
};

/* ---------- Step 3: Knowledge base ---------- */
const KnowledgeStep = ({ collections, selectedIds, setSelectedIds, files, setFiles, savedIds, newCollectionName, setNewCollectionName }) => {
  const inputRef = useRef(null);
  const toggle = (id) =>
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <div className="mt-6 space-y-5">
      {savedIds?.length > 0 && (
        <div className="inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2.5 text-[13px] font-semibold text-teal-800">
          <Check size={15} strokeWidth={3} /> {savedIds.length} knowledge collection{savedIds.length > 1 ? 's' : ''} ready.
        </div>
      )}

      {/* Upload new */}
      <div>
        <p className="text-[13px] font-semibold">Upload documents</p>
        <p className="mt-0.5 text-[12.5px] text-slate-400">PDF, DOCX, TXT — grounds your AI in your own content. Optional.</p>
        <div
          onClick={() => inputRef.current?.click()}
          className="mt-2 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center transition hover:border-teal-300 hover:bg-teal-50/40"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
            <Upload size={17} />
          </span>
          <p className="mt-2 text-[12.5px] font-semibold">Click to choose files</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px]">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <Paperclip size={13} className="shrink-0 text-slate-400" />
                  <span className="truncate">{f.name}</span>
                </span>
                <button
                  onClick={() => setFiles((cur) => cur.filter((_, idx) => idx !== i))}
                  className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-3">
            <label className="text-[12.5px] font-semibold text-slate-600">New collection name</label>
            <input
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              placeholder="e.g. Front Desk Docs"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
            />
            <p className="mt-1 text-[11.5px] text-slate-400">Your uploaded files are grouped into this new collection.</p>
          </div>
        )}
      </div>

      {/* Select existing */}
      {collections.length > 0 && (
        <div>
          <p className="text-[13px] font-semibold">Or use existing collections</p>
          <div className="mt-2 space-y-1">
            {collections.map((c) => {
              const id = String(c._id);
              const on = selectedIds.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-[13px] transition ${
                    on ? 'border-teal-300 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                  }`}
                >
                  <span className={`flex h-4 w-4 items-center justify-center rounded border ${on ? 'border-teal-600 bg-teal-600 text-white' : 'border-slate-300'}`}>
                    {on && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="truncate font-medium">{c.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-[12.5px] text-slate-400">Knowledge is optional — press Next to save your selection, or skip it.</p>
    </div>
  );
};

/* ---------- Step 4: Rules & Instructions — composed into the agent's system prompt ---------- */
const RULE_PERMISSIONS = [
  { key: 'answer', label: 'Answer questions', icon: MessagesSquare },
  { key: 'book', label: 'Book & schedule appointments', icon: CalendarDays },
  { key: 'modify', label: 'Modify bookings', icon: FileText },
  { key: 'cancel', label: 'Cancel bookings', icon: X },
  { key: 'leads', label: 'Capture leads & take messages', icon: Send },
  { key: 'sms', label: 'Send SMS confirmations', icon: MessageSquare },
  { key: 'transfer', label: 'Transfer calls to staff', icon: Phone },
];
const RULE_ESCALATIONS = [
  { key: 'person', label: 'Caller asks for a person' },
  { key: 'angry', label: 'Angry or dissatisfied caller' },
  { key: 'safety', label: 'Safety-critical or urgent issue' },
  { key: 'complex', label: 'Complex or out-of-scope request' },
  { key: 'vip', label: 'VIP or high-value inquiry' },
];
const RULE_TRANSFER_TARGETS = ['Manager', 'Front desk', 'On-call staff', 'Owner'];
const RULE_TIMING = ['Transfer immediately', 'Take a message after hours'];
const RULE_CHIPS = [
  { label: 'Business hours', text: 'Our hours are Monday–Friday, 9am–6pm.' },
  { label: 'Pricing note', text: 'Do not quote exact prices; give ranges only if provided.' },
  { label: 'Escalation policy', text: 'Escalate urgent or unhappy callers to a human right away.' },
  { label: 'Do-not list', text: 'Never share internal, personal, or payment information.' },
];

const RuleToggle = ({ on, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-teal-600' : 'bg-slate-200'}`}
  >
    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
  </button>
);

const composeRules = ({ instructions, perms, esc, transferTo, transferPhone, timing }) => {
  const onLabels = RULE_PERMISSIONS.filter((p) => perms[p.key]).map((p) => p.label);
  const offLabels = RULE_PERMISSIONS.filter((p) => !perms[p.key]).map((p) => p.label);
  const escLabels = RULE_ESCALATIONS.filter((e) => esc[e.key]).map((e) => e.label);
  const parts = [];
  if (instructions.trim()) parts.push(`Business instructions:\n${instructions.trim()}`);
  parts.push(`The agent is allowed to: ${onLabels.length ? onLabels.join(', ') : 'nothing configured'}.`);
  if (offLabels.length) parts.push(`The agent must NOT: ${offLabels.join(', ')}.`);
  if (escLabels.length) {
    parts.push(
      `Escalate to ${transferTo}${transferPhone ? ` (${transferPhone})` : ''} when: ${escLabels.join('; ')}. ${timing}.`
    );
  }
  return parts.join('\n\n');
};

const RulesStep = ({ setRules }) => {
  const [instructions, setInstructions] = useState('');
  const [perms, setPerms] = useState(() => Object.fromEntries(RULE_PERMISSIONS.map((p) => [p.key, true])));
  const [esc, setEsc] = useState(() => Object.fromEntries(RULE_ESCALATIONS.map((e) => [e.key, true])));
  const [transferTo, setTransferTo] = useState(RULE_TRANSFER_TARGETS[0]);
  const [transferPhone, setTransferPhone] = useState('');
  const [timing, setTiming] = useState(RULE_TIMING[0]);

  useEffect(() => {
    setRules(composeRules({ instructions, perms, esc, transferTo, transferPhone, timing }));
  }, [instructions, perms, esc, transferTo, transferPhone, timing, setRules]);

  const enabledCount = RULE_PERMISSIONS.filter((p) => perms[p.key]).length;
  const field = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-teal-500';

  return (
    <div className="mt-4">
      <h2 className="text-[20px] font-bold tracking-tight">Rules &amp; Instructions</h2>
      <p className="mt-1 text-[13px] text-slate-500">
        Define how your AI should operate — policies, permissions, and escalation. This is saved as the agent's overall system prompt.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* 1. Business instructions */}
        <div className="rounded-2xl border border-slate-200 p-5">
          <p className="text-[14px] font-bold">1. Business Instructions</p>
          <p className="mt-0.5 text-[12.5px] text-slate-500">Add special instructions, preferences, or exceptions unique to your business.</p>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={9}
            placeholder={'e.g.\n- For large parties, collect name, phone, and details, then send to a manager.\n- Do not book after 9:00 PM.\n- Mention free parking when asked for directions.'}
            className="mt-3 w-full resize-none rounded-xl border border-slate-200 px-3.5 py-3 text-[13px] leading-relaxed outline-none focus:border-teal-500"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {RULE_CHIPS.map((c) => (
              <button
                key={c.label}
                onClick={() => setInstructions((v) => (v.trim() ? `${v.trim()}\n- ${c.text}` : `- ${c.text}`))}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
              >
                + {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* 2 + 3 permissions & escalation */}
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 p-5">
            <p className="text-[14px] font-bold">2. What Your AI Can Do</p>
            <p className="mt-0.5 text-[12.5px] text-slate-500">Choose which actions your agent is allowed to handle.</p>
            <div className="mt-3 space-y-2">
              {RULE_PERMISSIONS.map((p) => (
                <div key={p.key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-2">
                  <span className="flex items-center gap-2 text-[13px]">
                    <p.icon size={15} className="text-slate-400" /> {p.label}
                  </span>
                  <RuleToggle on={perms[p.key]} onClick={() => setPerms((s) => ({ ...s, [p.key]: !s[p.key] }))} />
                </div>
              ))}
            </div>
            <p className="mt-2 text-right text-[12px] font-semibold text-emerald-600">
              {enabledCount} of {RULE_PERMISSIONS.length} permissions enabled
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-5">
            <p className="text-[14px] font-bold">3. Escalation Rules</p>
            <p className="mt-0.5 text-[12.5px] text-slate-500">Define when your AI should escalate to a human, and how.</p>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-[12px] font-semibold text-slate-500">Escalate when any of these happen</p>
                <div className="mt-2 space-y-1.5">
                  {RULE_ESCALATIONS.map((e) => (
                    <label key={e.key} className="flex items-center gap-2 text-[12.5px]">
                      <input
                        type="checkbox"
                        checked={esc[e.key]}
                        onChange={() => setEsc((s) => ({ ...s, [e.key]: !s[e.key] }))}
                        className="accent-teal-600"
                      />
                      {e.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-[12px] font-semibold text-slate-500">Transfer to</label>
                  <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)} className={`mt-1 ${field}`}>
                    {RULE_TRANSFER_TARGETS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-500">Phone</label>
                  <input value={transferPhone} onChange={(e) => setTransferPhone(e.target.value)} placeholder="(650) 555-1234" className={`mt-1 ${field}`} />
                </div>
                <div>
                  <label className="text-[12px] font-semibold text-slate-500">During business hours</label>
                  <select value={timing} onChange={(e) => setTiming(e.target.value)} className={`mt-1 ${field}`}>
                    {RULE_TIMING.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* Test — voice side: the agent calls the editable test number. */
const VoiceTestPanel = ({ scriptData }) => {
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [testNumber, setTestNumber] = useState(TEST_CALL_NUMBER);

  const startCall = async () => {
    if (!scriptData) return;
    if (!String(testNumber).replace(/\D/g, '')) {
      setErr('Enter a number to call.');
      return;
    }
    setStarting(true);
    setErr('');
    setOkMsg('');
    try {
      const token = localStorage.getItem('token');
      const placedByUserId = getCurrentUserIdForCalls();
      const body = {
        to: testNumber,
        from: DEFAULT_FROM_NUMBER,
        stack: 'kokoro-vad',
        preferBodyScript: true,
        ...(placedByUserId ? { placedByUserId } : {}),
        scriptData,
        fileId: null,
        voiceId: scriptData.settings?.voiceId || 'af_heart',
        ttsProvider: 'kokoro',
        useTen: true,
      };
      const res = await fetch(getOutboundCallUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        setErr('Server returned an invalid response.');
        return;
      }
      if (res.ok && data?.success) setOkMsg(`Call started · SID ${data.callSid}`);
      else setErr(data?.message || 'Failed to start call.');
    } catch (e) {
      setErr(`Error: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200">
      <div className="shrink-0 border-b border-slate-100 px-4 py-3">
        <h3 className="flex items-center gap-1.5 text-[14px] font-semibold">
          <Phone size={15} className="text-teal-600" /> Test voice call
        </h3>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <div>
          <p className="text-[12px] font-semibold text-slate-500">Test number</p>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 focus-within:border-teal-400">
            <Phone size={14} className="shrink-0 text-slate-400" />
            <input
              type="tel"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="+1 555 555 1234"
              className="w-full bg-transparent text-[14px] font-semibold text-slate-700 outline-none"
            />
          </div>
          <p className="mt-1 text-[11.5px] text-slate-400">The agent will call this number — edit it to dial your own phone.</p>
        </div>
        {err && <div className="rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-600">{err}</div>}
        {okMsg && <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-700">{okMsg}</div>}
      </div>
      <div className="shrink-0 border-t border-slate-100 p-4">
        <button
          onClick={startCall}
          disabled={starting || !scriptData}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
        >
          <Phone size={16} /> {starting ? 'Starting…' : 'Start call'}
        </button>
        <p className="mt-2 text-center text-[11.5px] text-slate-400">From: {DEFAULT_FROM_NUMBER}</p>
      </div>
    </div>
  );
};

/* ---------- Step 5: Test — voice on one side, chat on the other ---------- */
const TestStep = ({ scriptId }) => {
  const [scriptData, setScriptData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(`create-ai-${scriptId || 'x'}`);

  useEffect(() => {
    if (!scriptId) return;
    scriptsAPI
      .get(scriptId)
      .then((res) => setScriptData(res.data.script))
      .catch(() => {});
  }, [scriptId]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending || !scriptData) return;
    setInput('');
    const history = [...messages, { role: 'user', content: q }];
    setMessages(history);
    setSending(true);
    try {
      const res = await chatbotAPI.send({ sessionId: sessionRef.current, scriptData, fileId: null, query: q, messages: history });
      const data = res.data;
      setMessages((m) => [...m, { role: 'assistant', content: data?.success ? data.reply : `Error: ${data?.message || 'request failed'}` }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e.response?.data?.message || e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  if (!scriptId) {
    return <p className="text-[13px] text-slate-500">Create a script first (Step 2) to test your agent here.</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
      {/* Voice side */}
      <VoiceTestPanel scriptData={scriptData} />

      {/* Chat side */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200">
        <div className="shrink-0 border-b border-slate-100 px-4 py-3">
          <h3 className="flex items-center gap-1.5 text-[14px] font-semibold">
            <MessageSquare size={15} className="text-teal-600" /> Test chat
          </h3>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 p-4">
          {messages.length === 0 && (
            <p className="mt-8 text-center text-[13px] text-slate-400">
              {scriptData ? 'Say hello to test your agent.' : 'Loading script…'}
            </p>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13.5px] ${
                m.role === 'user' ? 'ml-auto rounded-br-sm bg-teal-600 text-white' : 'rounded-bl-sm bg-white text-slate-800 shadow-sm'
              }`}
            >
              {m.content}
            </div>
          ))}
          {sending && <div className="rounded-2xl rounded-bl-sm bg-white px-3.5 py-2 text-[13px] text-slate-400 shadow-sm">…</div>}
        </div>
        <div className="flex items-center gap-2 border-t border-slate-200 bg-white p-3">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Type a message…"
            className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
          />
          <button
            onClick={send}
            disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-teal-600 px-4 py-2.5 text-[13.5px] font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            <Send size={15} /> Send
          </button>
        </div>
      </div>
    </div>
  );
};

const STEPS = [
  { key: 'template', title: 'Choose a template', icon: LayoutTemplate, body: 'Start with a ready-made AI employee, or build from scratch. Your choice prefills the script and rules — you can change everything.' },
  { key: 'script', title: 'Script', icon: FileText, body: 'Review and edit what your AI says.' },
  { key: 'knowledge', title: 'Knowledge & Tools', icon: Upload, body: 'Add documents and connect tools your AI can use. Optional.' },
  { key: 'rules', title: 'Rules', icon: ShieldCheck, body: "Set the overall rules your AI must always follow — saved as the agent's system prompt." },
  { key: 'test', title: 'Test', icon: FlaskConical, body: 'Chat with your finished agent below, or place a test call.' },
];

const DemoCreateAI = () => {
  const navigate = useNavigate();
  const ownerId = getCurrentUserIdForCalls();
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1 — template + agent
  const [templates, setTemplates] = useState(AGENT_TEMPLATES);
  const [templateKey, setTemplateKey] = useState(null); // template key | 'scratch' | null
  const [agentName, setAgentName] = useState('');
  const [agentDesc, setAgentDesc] = useState('');
  const [agentIcon, setAgentIcon] = useState('bot');
  const [agentSettings, setAgentSettings] = useState({});
  const [createdAgent, setCreatedAgent] = useState(null);

  // Step 2 — script (created from the template in step 1)
  const [scriptMode, setScriptMode] = useState('prompt');
  const [promptText, setPromptText] = useState('');
  const [workflowJson, setWorkflowJson] = useState(null);
  const [scriptId, setScriptId] = useState(null);

  // Step 3 — knowledge
  const [collections, setCollections] = useState([]);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [kbFiles, setKbFiles] = useState([]);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [collectionIds, setCollectionIds] = useState([]);

  // Step 4 — rules (overall system prompt saved on the agent)
  const [rules, setRules] = useState('');

  const total = STEPS.length;
  const step = STEPS[index];
  const canPrev = index > 0;
  const canNext = index < total - 1;
  // Script (prompt/flow builder) and Test fill the page; other steps scroll.
  const isFillStep = step.key === 'script' || step.key === 'test';

  // Pick a template: remember it and prefill the agent name from it.
  const pickTemplate = (key) => {
    setTemplateKey(key);
    const t = key !== 'scratch' ? templates.find((x) => x.key === key) : null;
    if (t) {
      if (!agentName.trim()) setAgentName(t.name);
      setAgentDesc(t.description || '');
      setAgentIcon(t.icon || 'bot');
      setAgentSettings(t.settings || {});
    } else {
      setAgentDesc('');
      setAgentIcon('bot');
      setAgentSettings({});
    }
  };

  // Load existing knowledge collections once.
  useEffect(() => {
    knowledgeAPI
      .collections()
      .then((res) => setCollections(res.data?.collections || []))
      .catch(() => {});
  }, []);

  // Prefer DB-backed templates when available; fall back to the built-in set.
  useEffect(() => {
    templatesAPI
      .list()
      .then((r) => {
        if (r.data?.templates?.length) setTemplates(r.data.templates);
      })
      .catch(() => {});
  }, []);

  /* Persist the current step; return true to advance. */
  const persistStep = async () => {
    // Step 1 — choose a template: create the script (from the template) + the agent.
    if (step.key === 'template') {
      if (!templateKey) {
        alert('Pick a template, or choose “Start from scratch”.');
        return false;
      }
      if (!agentName.trim()) {
        alert('Give your agent a name.');
        return false;
      }
      if (createdAgent) return true;
      const t = templateKey !== 'scratch' ? templates.find((x) => x.key === templateKey) : null;
      const sType = t?.scriptType || 'Text Prompt';
      const sBody = t?.script || '';
      const scriptRes = await scriptsAPI.save({
        scriptName: `${agentName.trim()} Script`,
        type: sType,
        script: sBody,
      });
      const sid = scriptRes.data?.id;
      setScriptId(sid);
      setScriptMode(sType === 'Conversation Flow' ? 'workflow' : 'prompt');
      setPromptText(sBody);
      // Use the (possibly user-edited) basic settings captured in the template step.
      const agentRes = await agentsAPI.save({
        name: agentName.trim(),
        description: agentDesc,
        icon: agentIcon || 'bot',
        scriptId: sid,
        settings: agentSettings || {},
        collectionIds: [],
        status: 'draft',
      });
      const agent = agentRes.data?.agent || { _id: agentRes.data?.id, name: agentName.trim(), scriptId: sid };
      setCreatedAgent(agent);
      return true;
    }

    // Step 2 — script: save edits to the linked script.
    if (step.key === 'script') {
      if (!scriptId) return true;
      const script = scriptMode === 'workflow' ? (workflowJson || JSON.stringify(EMPTY_FLOW, null, 2)) : promptText;
      await scriptsAPI.update({ _id: scriptId, script });
      return true;
    }

    // Step 3 — knowledge & tools: upload/select collections, attach to the agent.
    if (step.key === 'knowledge') {
      const ids = [...selectedCollectionIds];
      if (kbFiles.length) {
        const fd = new FormData();
        kbFiles.forEach((f) => fd.append('files', f));
        const cname = newCollectionName.trim() || `${agentName.trim() || 'Agent'} Knowledge`;
        fd.append('collectionName', cname);
        await knowledgeAPI.upload(fd, ownerId);
        try {
          const cols = await knowledgeAPI.collections();
          setCollections(cols.data?.collections || []);
          const match = (cols.data?.collections || []).find((c) => c.name === cname);
          if (match?._id) ids.push(String(match._id));
        } catch {
          /* best effort */
        }
      }
      const finalIds = [...new Set(ids)];
      setCollectionIds(finalIds);
      if (createdAgent?._id) {
        await agentsAPI.update({ _id: createdAgent._id, collectionIds: finalIds });
      }
      return true;
    }

    // Step 4 — rules: save the composed system prompt onto the agent.
    if (step.key === 'rules') {
      if (createdAgent?._id) {
        await agentsAPI.update({
          _id: createdAgent._id,
          settings: { ...agentSettings, systemPrompt: rules },
        });
      }
      return true;
    }

    return true;
  };

  const handleNext = async () => {
    setBusy(true);
    try {
      const ok = await persistStep();
      if (ok) setIndex((i) => Math.min(total - 1, i + 1));
    } catch (e) {
      alert(`Couldn't save this step: ${e.response?.data?.error || e.response?.data?.message || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const nextLabel = () => {
    if (busy) return 'Saving…';
    switch (step.key) {
      case 'template':
        return createdAgent ? 'Next' : 'Create agent';
      case 'knowledge':
        return 'Save & continue';
      case 'rules':
        return 'Save & continue';
      default:
        return 'Next';
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Title */}
      <div className="shrink-0 pt-3">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-teal-600">
          <Sparkles size={12} /> Getting started
        </p>
        <h1 className="mt-0.5 text-[24px] font-bold tracking-tight">Create AI Agent</h1>
        <p className="mt-1 text-[14px] text-slate-500">
          Choose a template, refine the script, add knowledge & tools, set the rules, then test.
        </p>
      </div>

      {/* Stepper on top */}
      <div className="mt-4 flex shrink-0 items-start justify-between gap-0 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
        {STEPS.map((s, i) => {
          const done = i < index;
          const active = i === index;
          return (
            <React.Fragment key={s.title}>
              {i > 0 && <div className={`mt-4 h-[3px] flex-1 rounded-full ${i <= index ? 'bg-teal-600' : 'bg-slate-200'}`} />}
              <button
                onClick={() => {
                  if (i > index && !createdAgent) return; // can't skip ahead before creating the agent
                  setIndex(i);
                }}
                className="flex w-[64px] shrink-0 flex-col items-center gap-1.5 disabled:cursor-not-allowed sm:w-[112px]"
              >
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[13px] font-bold transition-all ${
                    done
                      ? 'border-teal-600 bg-teal-600 text-white'
                      : active
                      ? 'border-teal-600 bg-teal-50 text-teal-700 ring-4 ring-teal-600/15'
                      : 'border-slate-300 bg-white text-slate-400'
                  }`}
                >
                  {done ? <Check size={15} strokeWidth={3} /> : i + 1}
                </span>
                <span className={`text-center text-[10.5px] font-semibold leading-tight ${active ? 'text-teal-700' : done ? 'text-slate-700' : 'text-slate-400'}`}>
                  {s.title}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Active step — fills the page; builder/tester expand, other steps scroll */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="shrink-0 px-6 pt-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <step.icon size={20} />
            </span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Step {index + 1} of {total}</p>
              <h2 className="text-[18px] font-bold leading-tight">{step.title}</h2>
            </div>
          </div>
          <p className="mt-3 text-[14px] leading-relaxed text-slate-600">{step.body}</p>
        </div>

        {isFillStep ? (
          <div className="mt-3 flex min-h-0 flex-1 flex-col px-6 pb-6">
            {step.key === 'script' && (
              <ScriptEditStep
                mode={scriptMode}
                promptText={promptText}
                setPromptText={setPromptText}
                initialFlow={EMPTY_FLOW}
                workflowLoadKey="create-ai-workflow"
                scriptId={scriptId}
                onWorkflowChange={(json) => setWorkflowJson(json)}
              />
            )}
            {step.key === 'test' && <TestStep scriptId={scriptId} />}
          </div>
        ) : (
          <div className="demo-scroll mt-3 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6">
            {step.key === 'template' && (
              <TemplateChooseStep
                name={agentName}
                setName={setAgentName}
                desc={agentDesc}
                setDesc={setAgentDesc}
                icon={agentIcon}
                setIcon={setAgentIcon}
                settings={agentSettings}
                setSettings={setAgentSettings}
                templateKey={templateKey}
                onPick={pickTemplate}
                templates={templates}
              />
            )}
            {step.key === 'knowledge' && (
              <KnowledgeStep
                collections={collections}
                selectedIds={selectedCollectionIds}
                newCollectionName={newCollectionName}
                setNewCollectionName={setNewCollectionName}
                setSelectedIds={setSelectedCollectionIds}
                files={kbFiles}
                setFiles={setKbFiles}
                savedIds={collectionIds}
              />
            )}
            {step.key === 'rules' && <RulesStep rules={rules} setRules={setRules} />}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-200 pt-3">
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={!canPrev || busy}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13.5px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft size={17} /> Back
        </button>
        <div className="flex gap-2">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-2.5 rounded-full transition-all ${i === index ? 'w-8 bg-teal-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
        {canNext ? (
          <button
            onClick={handleNext}
            disabled={busy || (step.key === 'template' && !createdAgent && (!templateKey || !agentName.trim()))}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50"
          >
            {nextLabel()} <ChevronRight size={17} />
          </button>
        ) : (
          <button
            onClick={() => navigate('/dashboard/customer-ai/agents')}
            className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-sm hover:bg-teal-700"
          >
            <Check size={16} /> Done
          </button>
        )}
      </div>
    </div>
  );
};

export default DemoCreateAI;
